import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'components/Button';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import { GET_APP_EMBED_STATUS, GET_PROTECTION_SETTINGS } from 'gql/queries';
import { useHomeSetupRefresh } from 'hooks/useHomeSetupRefresh';
import { userRoles } from 'types/user';
import { isShipaidAdmin } from 'utils';
import { readThemeAppEmbedEvidence, themeAppEmbedMessage } from 'utils/themeAppEmbed';
import { readCurrentInstallationStatus } from 'utils/currentInstallationState';
import { InstallationStatusSummary } from 'components/InstallationPreferences/InstallationStatusSummary';
import { hasConnectedBilling } from 'utils/storeBillingPolicy';

type SetupStatus = 'complete' | 'incomplete' | 'checking' | 'unavailable';

const StoreSetup = () => {
  const navigate = useNavigate();
  const { storeId, storeProperties, storeLoading, refetchStoreProperties } =
    useStore();
  const { role, user } = useUser();
  const canEdit =
    role === userRoles.ADMIN ||
    role === userRoles.MANAGER ||
    isShipaidAdmin(user);
  const protection = useQuery(GET_PROTECTION_SETTINGS, {
    variables: { storeId },
    skip: !storeId,
    fetchPolicy: 'cache-only',
    notifyOnNetworkStatusChange: true,
  });
  const embed = useQuery(GET_APP_EMBED_STATUS, {
    variables: { storeId },
    skip: !storeId,
    fetchPolicy: 'cache-only',
    notifyOnNetworkStatusChange: true,
  });
  const [showCompleted, setShowCompleted] = useState(false);
  const setupCheck = useHomeSetupRefresh({
    storeId: storeId!,
    userId: user!.id,
    role,
    refetchStore:
      refetchStoreProperties ||
      (() => Promise.reject(new Error('Store refresh is unavailable'))),
  });

  const currentStore =
    storeProperties?.id === storeId ? storeProperties : undefined;
  const storeStatus: SetupStatus | null =
    setupCheck.cold ||
    (storeLoading && !currentStore) ||
    (storeProperties && !currentStore)
      ? 'checking'
      : !currentStore || setupCheck.errors.store
      ? 'unavailable'
      : null;
  const settings = protection.data?.protectionSettings?.find(
    (setting: { storeId: string }) => setting.storeId === storeId,
  );
  const protectionStatus: SetupStatus | null =
    setupCheck.cold ||
    (protection.loading && !protection.data) ||
    protection.variables?.storeId !== storeId ||
    (protection.data?.protectionSettings?.length > 0 && !settings)
      ? 'checking'
      : setupCheck.errors.protection ||
        protection.error ||
        !Array.isArray(protection.data?.protectionSettings)
      ? 'unavailable'
      : null;
  const feeConfigured = Boolean(
    settings && (settings.defaultFee != null || settings.percentage != null),
  );
  const connectionNeeded = !storeStatus && Boolean(currentStore?.uninstalledAt);
  const legacyEvidence = readThemeAppEmbedEvidence(embed.data?.getAppEmbedStatus, storeId!, currentStore?.store);
  const installation = readCurrentInstallationStatus(embed.data?.getAppEmbedStatus, storeId!, currentStore?.store);
  const installationUnavailable = Boolean(connectionNeeded || setupCheck.errors.embed || embed.error || installation.unavailable
    || storeStatus === 'unavailable' || embed.variables?.storeId !== storeId);
  const installationStatus = installationUnavailable ? undefined : installation.status;
  const embedEvidence = { ...legacyEvidence, status: installation.unavailable ? 'unavailable' as const
    : installationStatus?.activation.status ?? legacyEvidence.status };
  const embedEnabled = !connectionNeeded && embedEvidence.status === 'enabled';
  const embedDisabled = !connectionNeeded && embedEvidence.status === 'disabled';
  const embedStatus: SetupStatus =
    setupCheck.cold ||
    (embed.loading && !embedEnabled && !embedDisabled) ||
    embed.variables?.storeId !== storeId
      ? 'checking'
      : setupCheck.errors.embed ||
        embed.error || installation.unavailable ||
        (!embedEnabled && !embedDisabled)
      ? 'unavailable'
      : embedEnabled
      ? 'complete'
      : 'incomplete';
  const steps = [
    {
      id: 'connect',
      label: 'Connect store',
      status:
        storeStatus ||
        (currentStore?.store && !currentStore.uninstalledAt
          ? 'complete'
          : 'incomplete'),
      action: () => navigate('/settings/integration'),
    },
    {
      id: 'billing',
      label: 'Set up billing',
      status:
        storeStatus ||
        (hasConnectedBilling(currentStore)
          ? 'complete'
          : 'incomplete'),
      action: () => navigate('/settings/billing/subscription'),
    },
    {
      id: 'fee',
      label: 'Configure Delivery Guarantee fee',
      status:
        storeStatus ||
        protectionStatus ||
        (feeConfigured && currentStore?.completedSteps?.hasVisitedProtectionPage
          ? 'complete'
          : 'incomplete'),
      action: () => navigate('/settings/protection'),
    },
    {
      id: 'checkout',
      label: 'Enable ShipAid theme app embed',
      status: storeStatus || embedStatus,
      action: () => navigate('/settings/protection#installation'),
    },
  ];
  const completeCount = steps.filter(
    (step) => step.status === 'complete',
  ).length;
  const complete = completeCount === steps.length;
  const functionalVerified = !installationUnavailable && installationStatus?.functionalVerification.status === 'verified';
  const checking = steps.some((step) => step.status === 'checking');
  const unavailable = steps.some((step) => step.status === 'unavailable');
  const nextStep = steps.find((step) => step.status === 'incomplete');
  const expanded = !complete || showCompleted;

  return (
    <section
      aria-label="Store setup"
      className="rounded-xl border border-slate-200 bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-950">
            {complete && (
              <CheckCircle2
                aria-hidden="true"
                className="h-4 w-4 text-emerald-600"
              />
            )}
            {complete
              ? functionalVerified ? 'Installation verified' : 'Configuration saved'
              : 'Finish setting up ShipAid'}
          </h2>
          {complete ? (
            <p className="mt-1 text-sm text-slate-600">
              {functionalVerified ? 'Published theme and current configuration have functional checks recorded.' : 'Review the published installation and current cart checks.'}
            </p>
          ) : (
            <p className="mt-1 text-sm text-slate-600" aria-live="polite">
              {checking
                ? 'Checking setup status…'
                : `${completeCount} of 4 essential steps complete`}
            </p>
          )}
        </div>
        {complete ? (
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls="home-setup-steps"
            onClick={() => setShowCompleted(!showCompleted)}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {showCompleted ? 'Hide setup' : 'View setup'}
            {showCompleted ? (
              <ChevronUp aria-hidden="true" className="h-4 w-4" />
            ) : (
              <ChevronDown aria-hidden="true" className="h-4 w-4" />
            )}
          </button>
        ) : nextStep && nextStep.id !== 'checkout' && canEdit ? (
          <Button variant="outlined" onClick={nextStep.action}>{nextStep.label}</Button>
        ) : null}
      </div>
      <div className="px-5 pb-4">
        <InstallationStatusSummary status={installationStatus} activationStatus={embedEvidence.status}
          unavailable={installationUnavailable} checking={storeStatus === 'checking' || setupCheck.cold} />
        <div className="flex flex-col items-start gap-4 rounded-lg border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-950">
              Settings → Guarantee → Installation
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Choose your setup, customize Checkout+, and review theme activation and cart checks.
            </p>
          </div>
          <Link
            to="/settings/protection#installation"
            onClick={setupCheck.recheckOnReturn}
            className="inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-[#262626] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Open installation
          </Link>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-slate-100 px-5 py-4">
          <ol
            id="home-setup-steps"
            className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
          >
            {steps.map((step, index) => (
              <li key={step.id} className="flex items-start gap-2.5">
                <span
                  aria-hidden="true"
                  className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                    step.status === 'complete'
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {step.status === 'complete' ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    index + 1
                  )}
                </span>
                <div>
                  <p className="text-sm font-medium text-slate-800">
                    {step.label}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {step.status === 'complete'
                      ? 'Complete'
                      : step.status === 'checking'
                      ? 'Checking…'
                      : step.status === 'unavailable'
                      ? 'Status unavailable'
                      : step.id === nextStep?.id
                      ? 'Next step'
                      : 'Not started'}
                  </p>
                  {step.id === 'checkout' && step.status !== 'checking' && (
                    <p className="mt-1 text-xs text-slate-500">
                      {themeAppEmbedMessage(
                        connectionNeeded || (embedStatus === 'unavailable' && (embedEnabled || embedDisabled)) ? 'unavailable' : embedEvidence.status,
                        false, embedEvidence.reasonCode,
                      )}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
          {!canEdit && nextStep && (
            <p className="mt-4 text-sm text-slate-600">
              An administrator can finish these setup steps.
            </p>
          )}
          {unavailable && (
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
              <p role="alert" className="text-amber-800">
                Some setup status could not be checked.
              </p>
              <button
                type="button"
                onClick={setupCheck.refresh}
                disabled={setupCheck.refreshing}
                className="rounded font-medium text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Retry setup check
              </button>
            </div>
          )}
          {!unavailable && (
            <button
              type="button"
              onClick={setupCheck.refresh}
              disabled={setupCheck.refreshing}
              className="mt-4 rounded text-sm font-medium text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {setupCheck.refreshing
                ? 'Checking setup…'
                : 'Refresh setup status'}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export const HomeSetup = () => {
  const { storeId } = useStore();
  const { user, role } = useUser();
  return storeId && user?.id ? (
    <StoreSetup key={`${user.id}:${role}:${storeId}`} />
  ) : null;
};
